import { useCallback, useEffect, useRef, useState } from "react";
import type { DepositAddressDto, DepositDto } from "types/api/deposits";
import { isAbortError } from "utils/polling";
import { toastResponseError } from "utils/responseMessageHelper";
import {
  DEPOSIT_CHECK_POLL_MS,
  DEPOSIT_IDLE_POLL_MS,
  fetchDepositAddress,
  fetchDeposits,
  fetchDepositWallets,
  parseDepositFee,
  parseMinAmount,
  requestPaymentCheck,
  watchDeposits,
  type DepositFee,
} from "utils/usdtDeposits";
import {
  getUsdtCheckWindow,
  saveUsdtCheckWindow,
  type UsdtCheckWindow,
} from "utils/usdtDepositSession";

// setTimeout's ceiling; the windows are minutes long anyway.
const MAX_TIMER_MS = 2 ** 31 - 1;

type Options = {
  // RightPanel visibility — polling stops the moment the panel closes.
  isOpen: boolean;
  // Credited deposits that were not in the list when the screen opened
  // (newest first), plus the whole list. The baseline never counts as new.
  onNewDeposits?: (fresh: DepositDto[], all: DepositDto[]) => void;
};

type Loaded = {
  fee: DepositFee;
  minAmount: number;
  address: DepositAddressDto;
};

const isFuture = (instant: string | undefined, now: number) =>
  instant !== undefined && Date.parse(instant) > now;

// The user's permanent deposit address and the credited deposits behind it.
// Opening the screen requests the address (which also makes the backend scan
// it more often), then the list is polled while the panel is open: faster
// inside a payment check window, slower otherwise.
export const useUsdtDeposit = ({ isOpen, onNewDeposits }: Options) => {
  const [loaded, setLoaded] = useState<Loaded | null>(null);
  const [deposits, setDeposits] = useState<DepositDto[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [checkWindow, setCheckWindow] =
    useState<UsdtCheckWindow>(getUsdtCheckWindow);
  const [checkPending, setCheckPending] = useState(false);
  // Bumped to restart polling (a new check, or after it stopped on an error).
  const [watchRun, setWatchRun] = useState(0);
  // Re-renders when a check window or cooldown ends.
  const [, setClock] = useState(0);

  const mountedRef = useRef(true);
  const seenRef = useRef<Set<number>>(new Set());
  const callbackRef = useRef(onNewDeposits);
  callbackRef.current = onNewDeposits;

  useEffect(
    () => () => {
      mountedRef.current = false;
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    setLoadFailed(false);
    Promise.all([fetchDepositWallets(), fetchDepositAddress(), fetchDeposits()])
      .then(([wallets, address, list]) => {
        if (cancelled) return;
        seenRef.current = new Set(list.map((deposit) => deposit.id));
        setLoaded({
          fee: parseDepositFee(wallets),
          minAmount: parseMinAmount(address, wallets),
          address,
        });
        setDeposits(list);
      })
      .catch((error) => {
        if (cancelled) return;
        setLoadFailed(true);
        toastResponseError(error);
      });

    return () => {
      cancelled = true;
    };
  }, [loadAttempt]);

  const retryLoad = useCallback(() => setLoadAttempt((n) => n + 1), []);

  const ready = deposits !== null;
  const now = Date.now();
  const checking = isFuture(checkWindow.checkUntil, now);
  const throttled = isFuture(checkWindow.retryAt, now);

  useEffect(() => {
    if (!isOpen || !ready) return undefined;

    const controller = new AbortController();
    watchDeposits({
      signal: controller.signal,
      intervalMs: checking ? DEPOSIT_CHECK_POLL_MS : DEPOSIT_IDLE_POLL_MS,
      onList: (list) => {
        const fresh = list.filter(
          (deposit) => !seenRef.current.has(deposit.id),
        );
        if (fresh.length === 0) return;
        fresh.forEach((deposit) => seenRef.current.add(deposit.id));
        setDeposits(list);
        callbackRef.current?.(fresh, list);
      },
    }).catch((error) => {
      if (controller.signal.aborted || isAbortError(error)) return;
      // Transient errors are retried by the loop; anything else stops it
      // until the user checks again.
      toastResponseError(error);
    });

    return () => controller.abort();
  }, [isOpen, ready, checking, watchRun]);

  useEffect(() => {
    const now = Date.now();
    const next = [checkWindow.checkUntil, checkWindow.retryAt]
      .filter((instant) => isFuture(instant, now))
      .map((instant) => Date.parse(instant!) - now);
    if (next.length === 0) return undefined;

    const timer = setTimeout(
      () => setClock((n) => n + 1),
      Math.min(Math.min(...next) + 50, MAX_TIMER_MS),
    );
    return () => clearTimeout(timer);
  }, [checkWindow]);

  const updateCheckWindow = useCallback((next: UsdtCheckWindow) => {
    saveUsdtCheckWindow(next);
    setCheckWindow(next);
  }, []);

  const check = useCallback(async () => {
    if (checkPending || throttled) return;

    setCheckPending(true);
    try {
      const outcome = await requestPaymentCheck();
      if (!mountedRef.current) return;
      updateCheckWindow({
        checkUntil: outcome.checkUntil ?? checkWindow.checkUntil,
        retryAt: outcome.retryAt,
      });
      // Restarts a loop that stopped on an error; a new check window also
      // switches it to the faster pace.
      setWatchRun((n) => n + 1);
    } catch (error) {
      if (mountedRef.current) toastResponseError(error);
    } finally {
      if (mountedRef.current) setCheckPending(false);
    }
  }, [checkPending, checkWindow.checkUntil, throttled, updateCheckWindow]);

  return {
    fee: loaded?.fee ?? null,
    minAmount: loaded?.minAmount ?? 0,
    address: loaded?.address ?? null,
    // Credited deposits, newest first; null until the first load.
    deposits,
    loadFailed,
    retryLoad,
    checkUntil: checking ? checkWindow.checkUntil! : null,
    retryAt: throttled ? checkWindow.retryAt! : null,
    checkPending,
    check,
  };
};

export type UsdtDepositState = ReturnType<typeof useUsdtDeposit>;
